import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';

export default function Intake() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [messageText, setMessageText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Add user profile state
  const [userFullName, setUserFullName] = useState<string>('');

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      } else {
        // Load user profile name
        const metadata = session.user.user_metadata;
        const firstName = metadata.first_name || '';
        const lastName = metadata.last_name || '';
        setUserFullName(`${firstName} ${lastName}`.trim() || 'User');
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleExtractAndSave = async () => {
    if (!messageText.trim()) {
      alert('Please paste some data first');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Detect format and extract data
      const isFreeRateUpdate = messageText.includes('FreeRateUpdate') || messageText.includes('Rate Update');
      
      if (isFreeRateUpdate) {
        // Parse FreeRateUpdate format
        await parseFreeRateUpdate(messageText);
        alert('Data extracted and saved successfully!');
      } else {
        // Parse TM Leads format (can be multiple rows)
        const result = await parseTMLeads(messageText);
        if (result.success > 0) {
          alert(`Successfully imported ${result.success} client(s)!${result.failed > 0 ? `\n${result.failed} client(s) failed to import.` : ''}`);
        } else {
          alert('Failed to import any clients. Please check the data format.');
        }
      }
      
      setMessageText('');
    } catch (error) {
      console.error('Error processing data:', error);
      alert('Error processing data. Please check the format and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const parseFreeRateUpdate = async (text: string) => {
    try {
      // Extract name from the first line (e.g., "Dan Flok requested...")
      const nameMatch = text.match(/^FreeRateUpdate:\s*\n?(.+?)\s+requested/i);
      const name = nameMatch ? nameMatch[1].trim() : '';
      
      // Extract loan type and amount (e.g., "30-Yr Fixed purch quote for $520k")
      const loanTypeMatch = text.match(/requested\s+(.+?)\s+(?:purch|purchase)\s+quote/i);
      const loanType = loanTypeMatch ? loanTypeMatch[1].trim() : 'Conventional';
      
      const amountMatch = text.match(/for\s+\$(\d+(?:\.\d+)?)(k|m)?/i);
      let loanAmount = '0';
      if (amountMatch) {
        const amount = parseFloat(amountMatch[1]);
        const unit = amountMatch[2]?.toLowerCase();
        if (unit === 'k') {
          loanAmount = `$${(amount * 1000).toLocaleString()}`;
        } else if (unit === 'm') {
          loanAmount = `$${(amount * 1000000).toLocaleString()}`;
        } else {
          loanAmount = `$${amount.toLocaleString()}`;
        }
      }
      
      // Extract phone (e.g., "P: 6103900778")
      const phoneMatch = text.match(/P:\s*(\d+)/i);
      const phone = phoneMatch ? phoneMatch[1].trim() : '';
      
      // Extract email (e.g., "E: danflok1@gmail.com")
      const emailMatch = text.match(/E:\s*([^\s]+@[^\s]+)/i);
      const email = emailMatch ? emailMatch[1].trim() : '';
      
      if (!name || !email) {
        throw new Error('Could not extract required fields (name and email)');
      }

      // Format phone number
      let formattedPhone = phone;
      if (phone.length === 10) {
        formattedPhone = `(${phone.slice(0, 3)})-${phone.slice(3, 6)}-${phone.slice(6)}`;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const timestamp = new Date().toISOString();
      
      // Auto-populate first_name and last_name from name
      const nameParts = name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const clientData = {
        name,
        first_name: firstName,
        last_name: lastName,
        email,
        phone: formattedPhone || '-',
        state: '-',
        purchase_price: '-',
        loan_amount: loanAmount || '-',
        broker: 'NS',
        last_contact: 'None',
        client_state: 'Lead Received; Not Yet Contacted',
        date_created: timestamp,
        user_id: session.user.id,
        loan_type: '-',
        lead_source: 'FreeRateUpdate',
        client_state_updated: timestamp
      };

      // Save to Supabase
      const { error } = await supabase
        .from('clients')
        .insert([clientData]);

      if (error) throw error;
    } catch (error) {
      console.error('Error parsing FreeRateUpdate:', error);
      throw error;
    }
  };

  const parseTMLeads = async (text: string): Promise<{ success: number; failed: number }> => {
    let successCount = 0;
    let failedCount = 0;

    try {
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        throw new Error('No data found');
      }

      // Check if first line is a header (contains "First Name" or similar)
      const firstLine = lines[0].toLowerCase();
      const isHeader = firstLine.includes('first name') || firstLine.includes('firstname') || 
                       firstLine.includes('last name') || firstLine.includes('lastname') ||
                       firstLine.includes('cell phone') || firstLine.includes('email');
      
      // Start from index 1 if header exists, otherwise start from 0
      const startIndex = isHeader ? 1 : 0;
      
      // Process each data row
      for (let i = startIndex; i < lines.length; i++) {
        try {
          const dataLine = lines[i].split('\t').map(d => d.trim());
          
          // Skip empty lines
          if (dataLine.length < 4) {
            console.log(`Skipping line ${i + 1}: Not enough columns`);
            failedCount++;
            continue;
          }
          
          // Extract values based on position (0-indexed)
          // Expected order: First Name, Last Name, Cell Phone, Email, Street Address, City, Loan amount
          const firstName = dataLine[0] || '';
          const lastName = dataLine[1] || '';
          const cellPhone = dataLine[2] || '';
          const email = dataLine[3] || '';
          // Skip Street Address (index 4) and City (index 5)
          const loanAmountStr = dataLine[6] || '';
          
          // Combine first and last name
          const name = `${firstName} ${lastName}`.trim();
          
          // Validate required fields
          if (!name || !email) {
            console.log(`Skipping line ${i + 1}: Missing name or email`);
            failedCount++;
            continue;
          }
          
          // Parse loan amount
          let loanAmount = '0';
          if (loanAmountStr) {
            const cleaned = loanAmountStr.replace(/[^0-9.]/g, '');
            const parsed = parseFloat(cleaned);
            if (!isNaN(parsed)) {
              loanAmount = `$${parsed.toLocaleString()}`;
            }
          }
          
          // Format phone number
          let formattedPhone = cellPhone;
          const phoneDigits = cellPhone.replace(/\D/g, '');
          if (phoneDigits.length === 10) {
            formattedPhone = `(${phoneDigits.slice(0, 3)})-${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
          }

          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            throw new Error('Not authenticated');
          }

          const timestamp = new Date().toISOString();

          const clientData = {
            name,
            first_name: firstName,
            last_name: lastName,
            email,
            phone: formattedPhone || '-',
            state: '-',
            purchase_price: '-',
            loan_amount: loanAmount || '-',
            broker: 'TM',
            last_contact: 'None',
            client_state: 'Lead Received; Not Yet Contacted',
            date_created: timestamp,
            user_id: session.user.id,
            loan_type: '-',
            lead_source: 'TM Leads',
            client_state_updated: timestamp
          };

          // Save to Supabase
          const { error } = await supabase
            .from('clients')
            .insert([clientData]);

          if (error) {
            console.error(`Error saving client on line ${i + 1}:`, error);
            failedCount++;
          } else {
            successCount++;
          }
        } catch (error) {
          console.error(`Error processing line ${i + 1}:`, error);
          failedCount++;
        }
      }

      return { success: successCount, failed: failedCount };
    } catch (error) {
      console.error('Error parsing TM Leads:', error);
      throw error;
    }
  };

  const extractField = (lines: string[], fieldName: string): string => {
    const line = lines.find(l => l.includes(fieldName));
    if (!line) return '';
    return line.split(fieldName)[1]?.trim() || '';
  };

  const handleReset = () => {
    setMessageText('');
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-home-heart-line text-3xl text-navy"></i>
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'}`}>{userFullName || 'Loans with Heena'}</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            <a href="/crm" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-contacts-book-2-line mr-2"></i>
              CRM
            </a>
            
            {/* Pages Dropdown */}
            <div className="relative group">
              <button className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-1`}>
                <i className="ri-pages-line mr-2"></i>
                Pages
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                <a href="/closed" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-archive-line mr-2"></i>
                  Closed
                </a>
                <a href="/dead-deals" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-close-circle-line mr-2"></i>
                  Dead Deals
                </a>
                <a href="/on-hold" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-pause-circle-line mr-2"></i>
                  On Hold
                </a>
                <a href="/templates" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-file-text-line mr-2"></i>
                  Templates
                </a>
                <a href="/intake" className={`block px-4 py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} font-semibold cursor-pointer`}>
                  <i className="ri-inbox-line mr-2"></i>
                  Message Intake
                </a>
                <a href="/history" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-history-line mr-2"></i>
                  History
                </a>
                <a href="/admin" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-settings-3-line mr-2"></i>
                  Admin
                </a>
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>
            
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} hover:text-navy transition-colors cursor-pointer`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
          >
            <div className="px-6 py-4 space-y-3">
              <a href="/" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-home-5-line mr-2"></i>
                Home
              </a>
              <a href="/crm" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-contacts-book-2-line mr-2"></i>
                CRM
              </a>
              <a href="/closed" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-archive-line mr-2"></i>
                Closed
              </a>
              <a href="/dead-deals" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-close-circle-line mr-2"></i>
                Dead Deals
              </a>
              <a href="/on-hold" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-pause-circle-line mr-2"></i>
                On Hold
              </a>
              <a href="/templates" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-file-text-line mr-2"></i>
                Templates
              </a>
              <a href="/intake" className={`block py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                <i className="ri-inbox-line mr-2"></i>
                Message Intake
              </a>
              <a href="/history" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-history-line mr-2"></i>
                History
              </a>
              <a href="/admin" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-settings-3-line mr-2"></i>
                Admin
              </a>
              <button
                onClick={toggleDarkMode}
                className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-2`}></i>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left py-2 text-red-600 hover:text-red-700 transition-colors font-medium cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 text-center">
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Data Intake</h1>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Paste FreeRateUpdate messages or TM Leads data to automatically extract and save to CRM
          </p>
        </div>

        <div className={`w-full max-w-4xl mx-auto p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow border`}>
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Paste FreeRateUpdate message or TM Leads data here...&#10;&#10;The system will automatically detect which format you're using."
            className={`w-full h-64 px-4 py-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'border-gray-300 text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm font-mono resize-none`}
          />
          
          {/* Desktop buttons - side by side */}
          <div className="hidden md:flex gap-3 mt-4">
            <button
              onClick={handleExtractAndSave}
              disabled={isProcessing}
              className="flex-1 bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className={`${isProcessing ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} mr-2`}></i>
              {isProcessing ? 'Processing...' : 'Extract & Save to CRM'}
            </button>
            <button
              onClick={handleReset}
              disabled={isProcessing}
              className={`${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <i className="ri-refresh-line mr-2"></i>
              Reset
            </button>
          </div>
          
          {/* Mobile buttons - stacked */}
          <div className="flex md:hidden flex-col gap-3 mt-4">
            <button
              onClick={handleExtractAndSave}
              disabled={isProcessing}
              className="w-full bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className={`${isProcessing ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} mr-2`}></i>
              {isProcessing ? 'Processing...' : 'Extract & Save to CRM'}
            </button>
            <button
              onClick={handleReset}
              disabled={isProcessing}
              className={`w-full ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <i className="ri-refresh-line mr-2"></i>
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
